/**
 * Menu Action Template Generator
 * Analyzes DynamicMenuList and generates action templates for semantic action mapper
 */

import * as types from '../redux/actions/types';
import { ActionTemplate } from './semanticActionMapper';

interface MenuItem {
	id: number;
	primary?: boolean;
	header: string;
	screen: string;
	stack?: string;
	icon?: any;
	mobileIcon?: any;
	secondaryMenu?: {
		active: boolean;
		content?: Array<{
			id: number;
			parent: string;
			screen: string;
			header: string;
			path?: string;
			mobileIcon?: any;
			state?: boolean;
			new?: boolean;
		}>;
	};
	new?: boolean;
	default?: number;
	disabled?: boolean;
}

interface MenuSection {
	menuType: string;
	menuLabel: string;
	menu: MenuItem[];
}

/**
 * Generate navigation phrases for a menu item
 */
const generateNavigationPhrases = (
	header: string,
	screen: string
): string[] => {
	// Convert camelCase and PascalCase to readable text
	const readableHeader = header
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, (str) => str.toUpperCase())
		.trim()
		.toLowerCase();

	const readableScreen = screen
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, (str) => str.toUpperCase())
		.trim()
		.toLowerCase();

	// Remove common prefixes
	const cleanHeader = readableHeader.replace(/^menu\s+/i, '');

	return [
		`go to ${cleanHeader}`,
		`open ${cleanHeader}`,
		`show ${cleanHeader}`,
		`navigate to ${cleanHeader}`,
		`take me to ${cleanHeader}`,
		`view ${cleanHeader}`,
		`see ${cleanHeader}`,
		// Also include screen-based phrases
		`go to ${readableScreen}`,
		`open ${readableScreen}`,
		`show ${readableScreen}`,
	];
};

/**
 * Generate modal phrases for common actions
 */
const generateModalPhrases = (header: string, action: string): string[] => {
	const cleanHeader = header
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, (str) => str.toUpperCase())
		.trim()
		.toLowerCase()
		.replace(/^menu\s+/i, '');

	return [
		`${action} ${cleanHeader}`,
		`${action} new ${cleanHeader}`,
		`create ${cleanHeader}`,
		`add ${cleanHeader}`,
		`new ${cleanHeader}`,
		`start ${cleanHeader}`,
		`make ${cleanHeader}`,
	];
};

/**
 * Analyze menu and generate action templates
 */
export const generateActionTemplatesFromMenu = (
	menuData: MenuSection[]
): ActionTemplate[] => {
	const templates: ActionTemplate[] = [];

	menuData.forEach((section) => {
		section.menu.forEach((item) => {
			// Skip disabled items
			if (item.disabled) return;

			// Generate navigation template for primary menu items
			if (item.primary) {
				const stackName = item.stack || item.screen;
				const defaultScreen =
					item.secondaryMenu?.content?.[item.default || 0]?.screen ||
					item.screen;

				templates.push({
					intent: `NAVIGATE_TO_${item.screen.toUpperCase()}`,
					phrases: generateNavigationPhrases(
						item.header,
						item.screen
					),
					reduxAction: () => ({
						type: types.CURRENT_PAGE,
						payload: { route: { name: stackName } },
					}),
					actionSequence: [
						() => ({
							type: types.REACT_NAVIGATE,
							payload: {
								screen: defaultScreen,
								stack: stackName,
							},
						}),
					],
					category: 'navigation',
					requiresConfirmation: false,
					destructive: false,
					description: `Navigate to ${item.header}`,
				});

				// Generate templates for secondary menu items
				if (item.secondaryMenu?.active && item.secondaryMenu.content) {
					item.secondaryMenu.content.forEach((subItem) => {
						if (subItem.screen) {
							templates.push({
								intent: `NAVIGATE_TO_${subItem.screen.toUpperCase()}`,
								phrases: generateNavigationPhrases(
									subItem.header,
									subItem.screen
								),
								reduxAction: () => ({
									type: types.CURRENT_PAGE,
									payload: {
										route: {
											name: stackName,
											params: { screen: subItem.screen },
										},
									},
								}),
								actionSequence: [
									() => ({
										type: types.REACT_NAVIGATE,
										payload: {
											screen: subItem.screen,
											stack: stackName,
										},
									}),
								],
								category: 'navigation',
								requiresConfirmation: false,
								destructive: false,
								description: `Navigate to ${subItem.header}`,
							});
						}
					});
				}
			}
		});
	});

	// Generate common modal action templates
	const commonModalActions = [
		{
			screen: 'Projects',
			header: 'project',
			modalType: types.SHOW_PROJECT_MODAL,
			defaultSubScreen: 'ProjectMenu',
		},
		{
			screen: 'Data',
			header: 'data',
			modalType: 'SHOW_DATA_MODAL', // Placeholder
			defaultSubScreen: 'DataMenu',
		},
		{
			screen: 'Calendar',
			header: 'calendar event',
			modalType: types.SHOW_CALENDAR_ENTRY_MODAL,
			defaultSubScreen: 'Calendar',
		},
	];

	commonModalActions.forEach((modalAction) => {
		// Check if this stack exists in the menu
		const stackExists = menuData.some((section) =>
			section.menu.some(
				(item) =>
					item.screen === modalAction.screen ||
					item.stack === modalAction.screen
			)
		);

		if (stackExists) {
			templates.push({
				intent: modalAction.modalType,
				phrases: generateModalPhrases(modalAction.header, 'add'),
				reduxAction: () => ({ type: modalAction.modalType }),
				actionSequence: [
					// First navigate to the relevant page
					() => ({
						type: types.REACT_NAVIGATE,
						payload: {
							screen: modalAction.defaultSubScreen,
							stack: modalAction.screen,
						},
					}),
					// Then open the modal
					() => ({ type: modalAction.modalType }),
				],
				category: 'modal',
				requiresConfirmation: false,
				destructive: false,
				description: `Navigate to ${modalAction.header} and open creation form`,
			});
		}
	});

	return templates;
};

/**
 * Get all modal action types that might not be in the menu
 * These are standalone modal actions that don't require navigation
 */
export const getStandaloneModalTemplates = (): ActionTemplate[] => {
	return [
		{
			intent: types.SHOW_HELP_MODAL,
			phrases: [
				'show help',
				'open help',
				'I need help',
				'help me',
				'help documentation',
				'show documentation',
				'how do I use this',
			],
			reduxAction: () => ({ type: types.SHOW_HELP_MODAL }),
			category: 'modal',
			requiresConfirmation: false,
			destructive: false,
			description: 'Open help documentation',
		},
		{
			intent: types.SHOW_MESSAGE_MODAL,
			phrases: [
				'show messages',
				'open messages',
				'check messages',
				'view messages',
				'read messages',
			],
			reduxAction: () => ({ type: types.SHOW_MESSAGE_MODAL }),
			category: 'modal',
			requiresConfirmation: false,
			destructive: false,
			description: 'Open messages panel',
		},
		{
			intent: types.SHOW_NOTIFICATIONS_PANEL,
			phrases: [
				'show notifications',
				'check notifications',
				'open notifications',
				'view notifications',
				'notification panel',
			],
			reduxAction: () => ({ type: types.SHOW_NOTIFICATIONS_PANEL }),
			category: 'modal',
			requiresConfirmation: false,
			destructive: false,
			description: 'Open notifications panel',
		},
		{
			intent: types.SHOW_STICKY_NOTES_MODAL,
			phrases: [
				'show notes',
				'open notes',
				'sticky notes',
				'view notes',
				'my notes',
			],
			reduxAction: () => ({ type: types.SHOW_STICKY_NOTES_MODAL }),
			category: 'modal',
			requiresConfirmation: false,
			destructive: false,
			description: 'Open sticky notes',
		},
		{
			intent: types.PENDING_REFRESH,
			phrases: [
				'refresh',
				'reload',
				'refresh data',
				'reload data',
				'update data',
				'refresh the page',
				'reload the page',
			],
			reduxAction: () => ({ type: types.PENDING_REFRESH, payload: true }),
			category: 'data',
			requiresConfirmation: false,
			destructive: false,
			description: 'Refresh current data',
		},
		{
			intent: types.CHANGE_THEME,
			phrases: [
				'change theme',
				'switch theme',
				'toggle theme',
				'dark mode',
				'light mode',
				'switch to dark mode',
				'switch to light mode',
			],
			reduxAction: () => ({ type: types.CHANGE_THEME }),
			category: 'ui',
			requiresConfirmation: false,
			destructive: false,
			description: 'Toggle theme (dark/light mode)',
		},
		{
			intent: types.TOGGLE_FOCUS_MODE,
			phrases: [
				'focus mode',
				'enable focus mode',
				'distraction free',
				'hide distractions',
				'turn on focus mode',
			],
			reduxAction: () => ({ type: types.TOGGLE_FOCUS_MODE }),
			category: 'ui',
			requiresConfirmation: false,
			destructive: false,
			description: 'Toggle focus mode',
		},
	];
};
